// @ecliptica/i18n — recursos PT + EN em todas as apps (nunca texto no código).
// Textos comuns em common.<língua>.json (namespace "common"); cada app junta os seus (namespace "app").
// Só as línguas de ACTIVE_LANGS aparecem (ver langs.ts); os recursos de TODAS as LANGS são mantidos.
import i18next, { type i18n as I18n } from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import commonEn from "./common.en.json";
import commonPt from "./common.pt.json";
import { ACTIVE_LANGS, activeLangOr, DEFAULT_LANG, htmlLang, isActiveLang, LANG_STORAGE_KEY, type Lang } from "./langs";

export {
  ACTIVE_LANGS,
  activeLangOr,
  activeText,
  contentLangs,
  DEFAULT_LANG,
  filledInActiveLangs,
  htmlLang,
  intlLocale,
  isActiveLang,
  isLang,
  LANG_STORAGE_KEY,
  LANGS,
  mergeLangTexts,
  ogLocale,
  withoutInactiveLangs,
  type Lang,
  type LangTexts,
} from "./langs";

export const common = { pt: commonPt, en: commonEn };

type Dict = Record<string, unknown>;

/** Lista as chaves "a.b.c" de um objeto de recursos (para testar que PT e EN têm as mesmas). */
export function flattenKeys(o: Record<string, unknown>, prefix = ""): string[] {
  return Object.entries(o).flatMap(([k, v]) =>
    v && typeof v === "object" ? flattenKeys(v as Record<string, unknown>, `${prefix}${k}.`) : [`${prefix}${k}`],
  );
}

/**
 * Cria a instância i18next de uma app: common + os textos da app. Inicializa de forma síncrona
 * (os recursos já estão no bundle), o que permite pré-renderizar no servidor.
 * Carrega os recursos de todas as línguas mas só resolve para as ATIVAS: um browser em português,
 * um ?lang=pt ou uma escolha antiga guardada acabam na língua por omissão enquanto o PT estiver inativo.
 * - `lng` fixa a língua (site: vem do endereço) e desliga a deteção automática.
 * - `detector` (backoffice: i18next-browser-languagedetector): deteta por ?lang=, depois a escolha guardada,
 *   depois o navegador. Passa-se de fora para o site não carregar código que não usa.
 */
export function createI18n(app: Record<Lang, Dict>, opts: { lng?: Lang; detector?: Parameters<I18n["use"]>[0] } = {}): I18n {
  const inst = i18next.createInstance();
  if (!opts.lng && opts.detector) inst.use(opts.detector);
  void inst.use(initReactI18next).init({
    lng: opts.lng ? activeLangOr(opts.lng) : opts.detector ? undefined : DEFAULT_LANG,
    resources: {
      pt: { common: commonPt, app: app.pt },
      en: { common: commonEn, app: app.en },
    },
    supportedLngs: [...ACTIVE_LANGS],
    fallbackLng: DEFAULT_LANG,
    nonExplicitSupportedLngs: true,
    ns: ["app", "common"],
    defaultNS: "app",
    fallbackNS: "common",
    initAsync: false,
    interpolation: { escapeValue: false },
    detection: { order: ["querystring", "localStorage", "navigator"], lookupQuerystring: "lang", lookupLocalStorage: LANG_STORAGE_KEY, caches: ["localStorage"] },
  });
  inst.on("languageChanged", (l) => {
    if (typeof document !== "undefined") document.documentElement.lang = htmlLang(activeLangOr(l.slice(0, 2)));
  });
  return inst;
}

/**
 * Botão que passa para a língua ativa seguinte (as apps põem-no no cabeçalho).
 * Com menos de duas línguas ativas não mostra nada.
 * `onSwitch` substitui o comportamento por omissão — o site usa-o para mudar de endereço.
 */
export function LanguageSwitch({ className, onSwitch }: { className?: string; onSwitch?: (next: Lang) => void }) {
  const { i18n, t } = useTranslation("common");
  if (ACTIVE_LANGS.length < 2) return null;
  const cur: Lang = isActiveLang(i18n.resolvedLanguage) ? i18n.resolvedLanguage : DEFAULT_LANG;
  const next: Lang = ACTIVE_LANGS[(ACTIVE_LANGS.indexOf(cur) + 1) % ACTIVE_LANGS.length]!;
  return (
    <button
      type="button"
      className={className ?? "cursor-pointer rounded-md border border-border px-3 py-1.5 text-xs font-semibold hover:border-gold hover:text-gold-bright"}
      aria-label={`${t("lang.label")}: ${t(`lang.${next}`)}`}
      onClick={() => {
        try {
          localStorage.setItem(LANG_STORAGE_KEY, next);
        } catch {
          /* sem armazenamento: não faz mal */
        }
        if (onSwitch) onSwitch(next);
        else void i18n.changeLanguage(next);
      }}
    >
      {next.toUpperCase()}
    </button>
  );
}

export { useTranslation, Trans } from "react-i18next";
export { I18nextProvider } from "react-i18next";
